import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

// Read ?channel=<n> once for the initial useState value (before the router hook exists).
export const getInitialChannel = (channelsList = [], fallback) => {
  try {
    const p = new URLSearchParams(window.location.search).get("channel");
    const match = (channelsList || []).find((c) => String(c?.channel_number) === p);
    if (match) return match.channel_number;
  } catch (e) {
    /* ignore */
  }
  return fallback;
};

const isTypingTarget = (el) =>
  !!el &&
  (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.getAttribute?.("role") === "textbox");

// syncUrl=false leaves the address bar untouched (used while the overview is an opt-in trial).
export default function useChannelUrlSync({ channelNumbers = [], value, onChange, enableKeyboard = true, syncUrl = true }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const keys = channelNumbers.map(String);
  const keysSig = keys.join("|");

  const latest = useRef({ keys, channelNumbers, value, onChange });
  latest.current = { keys, channelNumbers, value, onChange };

  // URL -> state (pasted link, manual edit).
  useEffect(() => {
    const p = searchParams.get("channel");
    const { keys: k, channelNumbers: nums, value: v, onChange: change } = latest.current;
    if (p !== null && k.includes(p) && p !== String(v)) change?.(nums[k.indexOf(p)]);
  }, [searchParams, keysSig]);

  // state -> URL (replace, so channel switching does not flood browser history).
  useEffect(() => {
    if (!syncUrl) return;
    if (value === undefined || value === null || !keys.includes(String(value))) return;
    if (searchParams.get("channel") === String(value)) return;
    const next = new URLSearchParams(searchParams);
    next.set("channel", String(value));
    setSearchParams(next, { replace: true });
  }, [value, keysSig, syncUrl]);

  const step = useCallback((dir) => {
    const { keys: k, channelNumbers: nums, value: v, onChange: change } = latest.current;
    if (!k.length) return;
    const idx = Math.max(0, k.indexOf(String(v)));
    change?.(nums[(idx + dir + k.length) % k.length]);
  }, []);

  useEffect(() => {
    if (!enableKeyboard) return undefined;
    const onKey = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.defaultPrevented) return;
      if (isTypingTarget(document.activeElement)) return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      step(e.key === "ArrowLeft" ? -1 : 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enableKeyboard, step]);

  return { step };
}
